import { ebitdaAddbackGroup } from './cfo/ebitdaAddbacks'
import type {
  MetricComparison,
  MetricComparisonKind,
  MetricComparisonResponse,
  QuickBooksArTrendAnalysis,
  QuickBooksCfoPresentationResponse,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
} from '../../data/ServerApiClient'
import { visiblePresentationStatementRows } from './quickBooksCfoPresentationStatementRows'
import { buildPresentationDeckSlides, type DeckContentSlide } from './cfo/presentation/deckModel'
import { cfoCurrencyCode, cfoCurrencySymbol } from './cfo/cfoFormat'
import { buildRatioCardBlockModel, buildRatioCardSparkline, formatRatioCardValue } from './cfo/presentation/ratioCardBlock'
import { buildHighlightSvg } from './cfo/visuals/scene'
import { VISUAL_FONT_FACE_CSS } from './cfo/visuals/visualFontFace.generated'
import { visualRoleCssText } from './cfo/visuals/tokens'
import {
  presentationThemeCssText,
  resolvePresentationTheme,
  type ResolvedPresentationTheme,
} from './quickBooksCfoPresentationThemes'

type PresentationConfig = {
  version?: number
  reportTitle?: string
  periodCount?: number
  branding?: {
    reportTitle?: string
    subtitle?: string
    companyDisplayName?: string
    preparedReportTitle?: string
    preparedReportIcon?: string
  }
  sectionConfig?: Record<string, {
    enabled?: boolean
    title?: string
    aiEnabled?: boolean
    defaultPrompt?: string
    promptHints?: string
    promptAvoid?: string
  }>
  waterfalls?: Array<{
    id: string
    title: string
    mode: 'mom' | 'yoy' | 'ytd' | string
    enabled?: boolean
    aiEnabled?: boolean
    promptHints?: string
    promptAvoid?: string
  }>
  derivedMetrics?: {
    ebitda?: {
      enabled?: boolean
      interestEnabled?: boolean
      taxesEnabled?: boolean
      depreciationEnabled?: boolean
      amortizationEnabled?: boolean
      customRowHints?: string[]
    }
  }
  commentary?: {
    executiveSummary?: string
    sectionSummaries?: Record<string, string>
    alerts?: string[]
    actions?: string[]
    dataCaveats?: string[]
  }
  receivables?: {
    topCustomerLimit?: number
  }
  reportBlocks?: PresentationReportBlock[]
  style?: Record<string, any>
}

type PresentationReportBlockKind = 'key_highlights' | 'text' | 'kpi_grid' | 'statement_rollup' | 'statement_detail' | 'trend_chart' | 'waterfall' | 'ar_summary' | 'action_list' | 'ratio_card'

type PresentationReportBlock = {
  id: string
  kind: PresentationReportBlockKind
  title: string
  section: string
  enabled?: boolean
  source?: Record<string, any>
  layout?: Record<string, any>
  content?: Record<string, any>
}

type FinancialRollupRow = {
  key: string
  label: string
  values: Array<number | null>
  kind?: 'amount' | 'percent'
  emphasis?: 'section' | 'subtotal' | 'total' | 'muted'
}

const DEFAULT_PRESENTATION_PERIOD_COUNT = 6
const MAX_EXPORTED_PRESENTATION_PERIOD_COUNT = 6
const QUARTERLY_TREND_MONTH_COUNT = 24
const QUARTERLY_TREND_MAX_QUARTERS = 8

const cleanText = (value: any): string => String(value ?? '').trim()

const normalizeStringArray = (value: any): string[] => (
  Array.isArray(value)
    ? value.map(cleanText).filter(Boolean)
    : []
)

const DEFAULT_REPORT_BLOCKS: PresentationReportBlock[] = [
  { id: 'key-highlights', kind: 'key_highlights', title: 'Key Highlights', section: 'home', enabled: true },
  { id: 'home-kpis', kind: 'kpi_grid', title: 'Executive KPIs', section: 'home', enabled: true },
  { id: 'home-summary', kind: 'text', title: 'Executive Summary', section: 'home', enabled: true, source: { commentary: 'executiveSummary' } },
  { id: 'home-pnl-rollup', kind: 'statement_rollup', title: 'P&L Rollup', section: 'home', enabled: true, source: { statement_type: 'profit_and_loss' } },
  { id: 'home-quarterly-trend', kind: 'trend_chart', title: 'Quarterly Revenue & Net Income Trend', section: 'home', enabled: true, source: { statement_type: 'profit_and_loss', metric: 'quarterly_revenue_net_income' } },
  { id: 'pnl-rollup', kind: 'statement_rollup', title: 'Profit & Loss Rollup', section: 'profitAndLoss', enabled: true, source: { statement_type: 'profit_and_loss' } },
  { id: 'balance-sheet-rollup', kind: 'statement_rollup', title: 'Balance Sheet Rollup', section: 'balanceSheet', enabled: true, source: { statement_type: 'balance_sheet' } },
  { id: 'cash-flow-rollup', kind: 'statement_rollup', title: 'Cash Flow Rollup', section: 'cashFlow', enabled: true, source: { statement_type: 'cash_flow' } },
  { id: 'ar-summary', kind: 'ar_summary', title: 'A/R Trends', section: 'accountsReceivable', enabled: true },
  { id: 'pnl-detail', kind: 'statement_detail', title: 'Statement Detail', section: 'profitAndLoss', enabled: true, source: { statement_type: 'profit_and_loss' }, layout: { limit: 32 } },
  { id: 'actions-list', kind: 'action_list', title: 'Actions & Notes', section: 'actions', enabled: true },
]

const normalizeReportBlockKind = (value: any): PresentationReportBlockKind => {
  const kind = cleanText(value)
  if (kind === 'key_highlights' || kind === 'kpi_grid' || kind === 'statement_rollup' || kind === 'statement_detail' || kind === 'trend_chart' || kind === 'waterfall' || kind === 'ar_summary' || kind === 'action_list' || kind === 'ratio_card') return kind
  return 'text'
}

const builtInTextBlockSource = (
  id: string,
  kind: PresentationReportBlockKind,
  section: string,
  title: string,
): Record<string, string> | undefined => {
  if (kind !== 'text') return undefined
  const titleKey = cleanText(title).toLowerCase()
  if (id === 'home-summary' || (section === 'home' && titleKey === 'executive summary')) {
    return { commentary: 'executiveSummary' }
  }
  if (id === 'pnl-summary') return { commentary_section: 'profitAndLoss' }
  if (id === 'balance-sheet-summary') return { commentary_section: 'balanceSheet' }
  if (id === 'cash-flow-summary') return { commentary_section: 'cashFlow' }
  return undefined
}

const normalizeReportBlocks = (raw: any): PresentationReportBlock[] => {
  const entries = Array.isArray(raw) && raw.length ? raw : DEFAULT_REPORT_BLOCKS
  const normalized: PresentationReportBlock[] = entries.map((entry: any, index: number) => {
    const id = cleanText(entry?.id) || `report-block-${index + 1}`
    const kind = normalizeReportBlockKind(entry?.kind)
    const title = cleanText(entry?.title) || 'Report Block'
    const section = cleanText(entry?.section) || 'home'
    return {
      id,
      kind,
      title,
      section,
      enabled: entry?.enabled !== false,
      source: builtInTextBlockSource(id, kind, section, title)
        || (entry?.source && typeof entry.source === 'object' ? entry.source : undefined),
      layout: entry?.layout && typeof entry.layout === 'object' ? entry.layout : undefined,
      content: entry?.content && typeof entry.content === 'object' ? entry.content : undefined,
    }
  }).slice(0, 24)
  if (!normalized.some((block) => block.kind === 'key_highlights')) normalized.unshift(DEFAULT_REPORT_BLOCKS[0])
  return normalized
}

const displayReportTitle = (value: any): string => {
  const cleaned = cleanText(value)
  if (!cleaned) return 'Financial Review'
  if (!/\b(client|cfo)\b/i.test(cleaned)) return cleaned
  const scrubbed = cleaned
    .replace(/\b(client|cfo)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!scrubbed || /^financials?$/i.test(scrubbed) || /^packet$/i.test(scrubbed)) {
    return 'Financial Review'
  }
  return scrubbed
}

const normalizeConfig = (raw: any): PresentationConfig => {
  const src = raw && typeof raw === 'object' ? raw : {}
  const commentary = src.commentary && typeof src.commentary === 'object' ? src.commentary : {}
  const branding = src.branding && typeof src.branding === 'object' ? src.branding : {}
  const sectionSummaries = commentary.sectionSummaries && typeof commentary.sectionSummaries === 'object'
    ? Object.fromEntries(Object.entries(commentary.sectionSummaries).map(([key, value]) => [key, cleanText(value)]).filter(([, value]) => value))
    : {}
  const ebitda = src.derivedMetrics?.ebitda && typeof src.derivedMetrics.ebitda === 'object' ? src.derivedMetrics.ebitda : {}
  return {
    ...src,
    version: 2,
    reportTitle: cleanText(branding.reportTitle) || cleanText(src.reportTitle) || 'Financial Review',
    branding: {
      reportTitle: cleanText(branding.reportTitle) || cleanText(src.reportTitle) || 'Financial Review',
      subtitle: cleanText(branding.subtitle) || 'Performance review and management discussion',
      companyDisplayName: cleanText(branding.companyDisplayName),
      preparedReportTitle: cleanText(branding.preparedReportTitle) || 'Prepared Report',
      preparedReportIcon: cleanText(branding.preparedReportIcon) || 'building',
    },
    derivedMetrics: {
      ebitda: {
        enabled: ebitda.enabled !== false,
        interestEnabled: ebitda.interestEnabled !== false,
        taxesEnabled: ebitda.taxesEnabled !== false,
        depreciationEnabled: ebitda.depreciationEnabled !== false,
        amortizationEnabled: ebitda.amortizationEnabled !== false,
        customRowHints: normalizeStringArray(ebitda.customRowHints),
      },
    },
    periodCount: Math.max(1, Math.min(24, Math.floor(Number(src.periodCount) || DEFAULT_PRESENTATION_PERIOD_COUNT))),
    receivables: {
      topCustomerLimit: Math.max(1, Math.min(20, Math.floor(Number(src.receivables?.topCustomerLimit) || 5))),
    },
    reportBlocks: normalizeReportBlocks(src.reportBlocks),
    commentary: {
      executiveSummary: cleanText(commentary.executiveSummary),
      sectionSummaries,
      alerts: normalizeStringArray(commentary.alerts),
      actions: normalizeStringArray(commentary.actions),
      dataCaveats: normalizeStringArray(commentary.dataCaveats),
    },
  }
}

const labelToken = (value: any): string => cleanText(value)
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ')

const formatPeriod = (value: string | null | undefined): string => {
  if (!value) return ''
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}

const statementPeriods = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
): { periods: string[]; start: number } => {
  const allPeriods = Array.isArray(statement?.periods) ? statement.periods : []
  const bounded = Math.max(1, Math.min(24, Math.floor(Number(periodCount) || DEFAULT_PRESENTATION_PERIOD_COUNT)))
  const periods = allPeriods.slice(-bounded)
  return { periods, start: Math.max(0, allPeriods.length - periods.length) }
}

const rowSemanticKey = (row: QuickBooksFpaStatementRow | null | undefined): string => {
  const existing = cleanText((row as any)?.semantic_key)
  if (existing) return existing
  const label = labelToken(row?.label)
  if (/^net income$|^net ordinary income$|^net earnings$|^net profit$/.test(label)) return 'net_income'
  if (label === 'gross profit') return 'gross_profit'
  if (/^total revenue$|^total income$|^total sales$/.test(label)) return 'total_revenue'
  if (/^revenue$|^income$|^sales$/.test(label)) return 'revenue'
  if (/^total cost of sales$|^total cost of goods sold$|^total cogs$/.test(label)) return 'total_cost_of_sales'
  if (/^total operating expenses$|^operating expenses$|^total expenses$|^g and a expenses$|^general and administrative expenses$/.test(label)) return 'total_operating_expenses'
  if (/^cash and cash equivalents$|^total cash$|^cash$|^checking$|^savings$/.test(label)) return 'cash'
  if (/^total current assets$/.test(label)) return 'total_current_assets'
  if (/^total assets$/.test(label)) return 'total_assets'
  if (/^total current liabilities$/.test(label)) return 'total_current_liabilities'
  if (/^total liabilities$/.test(label)) return 'total_liabilities'
  if (/^total equity$/.test(label)) return 'total_equity'
  if (/^total liabilities and equity$|^total liabilities equity$|^total liabilities and shareholders equity$/.test(label)) return 'total_liabilities_equity'
  if (/^beginning cash/.test(label)) return 'beginning_cash'
  if (/^ending cash|^cash at end of period|^ending cash balance/.test(label)) return 'ending_cash'
  if (/^net change in cash|^change in cash|^net cash change/.test(label)) return 'net_change_cash'
  if (/^net cash .*operating|^cash from operations|^operating cash flow/.test(label)) return 'net_cash_operating'
  if (/^net cash .*investing|^cash from investing/.test(label)) return 'net_cash_investing'
  if (/^net cash .*financing|^cash from financing/.test(label)) return 'net_cash_financing'
  return ''
}

const findStatementRow = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  candidates: string[],
): QuickBooksFpaStatementRow | null => {
  const rows = (Array.isArray(statement?.rows) ? statement.rows : []).filter((row) => row && row.row_type !== 'spacer')
  const semanticCandidates = new Set(candidates.map(labelToken))
  const labelCandidates = candidates.map(labelToken).filter(Boolean)
  const passes = [
    (row: QuickBooksFpaStatementRow) => semanticCandidates.has(labelToken(rowSemanticKey(row))) && row.is_total,
    (row: QuickBooksFpaStatementRow) => semanticCandidates.has(labelToken(rowSemanticKey(row))),
    (row: QuickBooksFpaStatementRow) => labelCandidates.includes(labelToken(row.label)) && row.is_total,
    (row: QuickBooksFpaStatementRow) => labelCandidates.includes(labelToken(row.label)),
    (row: QuickBooksFpaStatementRow) => row.is_total && labelCandidates.some((candidate) => labelToken(row.label).includes(candidate)),
    (row: QuickBooksFpaStatementRow) => labelCandidates.some((candidate) => labelToken(row.label).includes(candidate)),
  ]
  for (const pass of passes) {
    const match = rows.find(pass)
    if (match) return match
  }
  return null
}

const rowPeriodValues = (
  row: QuickBooksFpaStatementRow | null | undefined,
  start: number,
  count: number,
): Array<number | null> => {
  const values = Array.isArray(row?.values) ? row.values : []
  return Array.from({ length: count }, (_, index) => {
    const value = Number(values[start + index])
    return Number.isFinite(value) ? value : null
  })
}

const zipSeries = (
  a: Array<number | null>,
  b: Array<number | null>,
  op: (left: number, right: number) => number,
): Array<number | null> => a.map((left, index) => {
  const right = b[index]
  return Number.isFinite(Number(left)) && Number.isFinite(Number(right)) ? op(Number(left), Number(right)) : null
})

// A profit margin is not meaningful when revenue is zero or negative. Showing
// a mechanically calculated percentage in that case turns a small credit or
// reversal into a multi-thousand-percent "margin" and is actively misleading
// in a board report. Keep the underlying amounts, but render the ratio as N/M.
const marginSeries = (numerator: Array<number | null>, revenue: Array<number | null>): Array<number | null> => (
  numerator.map((value, index) => {
    const den = Number(revenue[index])
    const num = Number(value)
    return Number.isFinite(num) && Number.isFinite(den) && den > 0.000001 ? num / den : null
  })
)

const seriesHasValue = (values: Array<number | null>): boolean =>
  values.some((value) => Number.isFinite(Number(value)) && Math.abs(Number(value)) > 0.000001)

const rowClassifierToken = (row: QuickBooksFpaStatementRow | null | undefined): string => labelToken([
  row?.qbo_account_type,
  row?.qbo_account_sub_type,
  row?.qbo_classification,
  row?.label,
].filter(Boolean).join(' '))

const rowMatchesAddbackGroup = (row: QuickBooksFpaStatementRow, group: string): boolean => ebitdaAddbackGroup(row) === group

const addbackSeries = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  start: number,
  count: number,
  group: string,
): Array<number | null> => {
  const rows = (Array.isArray(statement?.rows) ? statement.rows : []).filter((row) => rowMatchesAddbackGroup(row, group))
  const detailRows = rows.filter((row) => !row.is_total && row.row_type !== 'group' && row.row_type !== 'heading')
  const sourceRows = detailRows.length ? detailRows : rows
  if (!sourceRows.length) return Array.from({ length: count }, () => null)
  return Array.from({ length: count }, (_, index) => {
    const sum = sourceRows.reduce((total, row) => {
      const value = Number(row.values?.[start + index])
      return Number.isFinite(value) ? total + Math.abs(value) : total
    }, 0)
    return Math.abs(sum) > 0.000001 ? sum : null
  })
}

const calculateEbitdaSeries = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  start: number,
  count: number,
  netIncome: Array<number | null>,
  config?: PresentationConfig['derivedMetrics'],
): Array<number | null> => {
  const ebitdaConfig = config?.ebitda
  if (ebitdaConfig?.enabled === false) return Array.from({ length: count }, () => null)
  const enabledGroups = [
    ['interest', ebitdaConfig?.interestEnabled !== false],
    ['taxes', ebitdaConfig?.taxesEnabled !== false],
    ['depreciation', ebitdaConfig?.depreciationEnabled !== false],
    ['amortization', ebitdaConfig?.amortizationEnabled !== false],
  ] as const
  const addbacks = enabledGroups
    .filter(([, enabled]) => enabled)
    .map(([group]) => addbackSeries(statement, start, count, group))
  return Array.from({ length: count }, (_, index) => {
    const base = Number(netIncome[index])
    if (!Number.isFinite(base)) return null
    const value = base + addbacks.reduce((sum, series) => {
      const entry = Number(series[index])
      return Number.isFinite(entry) ? sum + entry : sum
    }, 0)
    return Number.isFinite(value) ? value : null
  })
}

const rollupRow = (
  key: string,
  label: string,
  values: Array<number | null>,
  options: Pick<FinancialRollupRow, 'kind' | 'emphasis'> = {},
): FinancialRollupRow => ({ key, label, values, kind: options.kind || 'amount', emphasis: options.emphasis })

export const profitAndLossRollupRows = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
  derivedMetrics?: PresentationConfig['derivedMetrics'],
): { periods: string[]; rows: FinancialRollupRow[] } => {
  const { periods, start } = statementPeriods(statement, periodCount)
  const count = periods.length
  const revenue = rowPeriodValues(findStatementRow(statement, ['total_revenue', 'total revenue', 'total income', 'revenue', 'income']), start, count)
  const grossProfit = rowPeriodValues(findStatementRow(statement, ['gross_profit', 'gross profit']), start, count)
  const netIncome = rowPeriodValues(findStatementRow(statement, ['net_income', 'net income', 'net earnings', 'net profit']), start, count)
  const costOfSalesSource = rowPeriodValues(findStatementRow(statement, ['total_cost_of_sales', 'total cost of sales', 'total cost of goods sold', 'total cogs', 'cost of sales']), start, count)
  const operatingExpenseSource = rowPeriodValues(findStatementRow(statement, ['total_operating_expenses', 'total operating expenses', 'operating expenses', 'total expenses', 'g&a expenses', 'general and administrative expenses']), start, count)
  const costOfSales = seriesHasValue(costOfSalesSource) ? costOfSalesSource : zipSeries(revenue, grossProfit, (rev, gp) => rev - gp)
  const operatingExpenses = seriesHasValue(operatingExpenseSource) ? operatingExpenseSource : zipSeries(grossProfit, netIncome, (gp, ni) => gp - ni)
  const ebitda = calculateEbitdaSeries(statement, start, count, netIncome, derivedMetrics)
  const rows = [
    rollupRow('revenue', 'Total Revenue', revenue, { emphasis: 'section' }),
    rollupRow('cost-of-sales', 'Cost of Sales', costOfSales),
    rollupRow('gross-profit', 'Gross Profit', grossProfit, { emphasis: 'subtotal' }),
    rollupRow('operating-expenses', 'Operating Expenses', operatingExpenses),
    ...(seriesHasValue(ebitda) ? [rollupRow('ebitda', 'EBITDA', ebitda, { emphasis: 'subtotal' })] : []),
    rollupRow('net-income', 'Net Income', netIncome, { emphasis: 'total' }),
    rollupRow('gross-margin', 'Gross Margin', marginSeries(grossProfit, revenue), { kind: 'percent', emphasis: 'muted' }),
    rollupRow('net-margin', 'Net Margin', marginSeries(netIncome, revenue), { kind: 'percent', emphasis: 'muted' }),
  ].filter((row) => seriesHasValue(row.values))
  return { periods, rows }
}

export const balanceSheetRollupRows = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
): { periods: string[]; rows: FinancialRollupRow[] } => {
  const { periods, start } = statementPeriods(statement, periodCount)
  const count = periods.length
  const rowFor = (candidates: string[]) => rowPeriodValues(findStatementRow(statement, candidates), start, count)
  const rows = [
    rollupRow('cash', 'Cash', rowFor(['cash', 'cash and cash equivalents', 'total cash'])),
    rollupRow('current-assets', 'Total Current Assets', rowFor(['total_current_assets', 'total current assets']), { emphasis: 'subtotal' }),
    rollupRow('assets', 'Total Assets', rowFor(['total_assets', 'total assets']), { emphasis: 'total' }),
    rollupRow('current-liabilities', 'Total Current Liabilities', rowFor(['total_current_liabilities', 'total current liabilities']), { emphasis: 'subtotal' }),
    rollupRow('liabilities', 'Total Liabilities', rowFor(['total_liabilities', 'total liabilities']), { emphasis: 'total' }),
    rollupRow('equity', 'Total Equity', rowFor(['total_equity', 'total equity']), { emphasis: 'total' }),
    rollupRow('liabilities-equity', 'Liabilities & Equity', rowFor(['total_liabilities_equity', 'total liabilities and equity']), { emphasis: 'muted' }),
  ].filter((row) => seriesHasValue(row.values))
  return { periods, rows }
}

export const cashFlowRollupRows = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
): { periods: string[]; rows: FinancialRollupRow[] } => {
  const { periods, start } = statementPeriods(statement, periodCount)
  const count = periods.length
  const rowFor = (candidates: string[]) => rowPeriodValues(findStatementRow(statement, candidates), start, count)
  const rows = [
    rollupRow('beginning-cash', 'Beginning Cash', rowFor(['beginning_cash', 'beginning cash'])),
    rollupRow('operating-cash', 'Cash from Operations', rowFor(['net_cash_operating', 'net cash from operating activities', 'cash from operations', 'operating cash flow']), { emphasis: 'subtotal' }),
    rollupRow('investing-cash', 'Cash from Investing', rowFor(['net_cash_investing', 'net cash from investing activities', 'cash from investing'])),
    rollupRow('financing-cash', 'Cash from Financing', rowFor(['net_cash_financing', 'net cash from financing activities', 'cash from financing'])),
    rollupRow('net-change', 'Net Change in Cash', rowFor(['net_change_cash', 'net change in cash', 'change in cash', 'net cash change']), { emphasis: 'total' }),
    rollupRow('ending-cash', 'Ending Cash', rowFor(['ending_cash', 'ending cash balance', 'cash at end of period', 'ending cash']), { emphasis: 'total' }),
  ].filter((row) => seriesHasValue(row.values))
  return { periods, rows }
}

const metricAt = (metrics: any, path: string): number | null => {
  const value = path.split('.').reduce((acc, part) => acc?.[part], metrics)
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const formatAmount = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  const abs = Math.abs(num)
  const scaled = abs >= 1_000_000 ? num / 1_000_000 : abs >= 1_000 ? num / 1_000 : num
  const suffix = abs >= 1_000_000 ? 'M' : abs >= 1_000 ? 'K' : ''
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: abs >= 1_000 ? 1 : 0,
    maximumFractionDigits: abs >= 1_000 ? 1 : 0,
  }).format(Math.abs(scaled))
  const symbol = cfoCurrencySymbol()
  return num < 0 ? `(${symbol}${formatted}${suffix})` : `${symbol}${formatted}${suffix}`
}

const formatSignedAmount = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  if (Math.abs(num) < 0.5) return `${cfoCurrencySymbol()}0`
  return num > 0 ? `+${formatAmount(num)}` : formatAmount(num)
}

const formatFullAmount = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return ''
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: cfoCurrencyCode(),
    maximumFractionDigits: 0,
  }).format(Math.abs(num))
  return num < 0 ? `(${formatted})` : formatted
}

const formatPct = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(num * 100)}%`
}

const formatDays = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(num)} days`
}

const safeClientRatio = (numerator: any, denominator: any): number | null => {
  const num = Number(numerator)
  const den = Number(denominator)
  return Number.isFinite(num) && Number.isFinite(den) && Math.abs(den) > 0.000001 ? num / den : null
}

const numberOrNull = (value: any): number | null => {
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const formatRollupValue = (value: number | null | undefined, kind: FinancialRollupRow['kind'] = 'amount'): string => (
  kind === 'percent' ? formatPct(value) : formatFullAmount(value)
)

const statementLatestPeriod = (statement: QuickBooksFpaStatementRender | null | undefined): string => {
  const periods = Array.isArray(statement?.periods) ? statement.periods : []
  return formatPeriod(periods[periods.length - 1])
}

const escapeHtml = (value: any): string => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

const inlineEmphasisHtml = (value: any): string => {
  const text = cleanText(value)
  const parts = text.split('**')
  if (parts.length === 1 || (parts.length - 1) % 2 !== 0) return escapeHtml(text)
  return parts.map((part, index) => (
    index % 2 === 1 ? `<strong>${escapeHtml(part)}</strong>` : escapeHtml(part)
  )).join('')
}

const statementRowHtml = (row: QuickBooksFpaStatementRow, periodCount: number): string => {
  const values = Array.isArray(row.values) ? row.values : []
  const latest = values.slice(Math.max(0, values.length - periodCount))
  return `<tr class="${row.is_total ? 'is-total' : ''}"><th style="padding-left:${12 + Math.max(0, Number(row.level) || 0) * 14}px">${escapeHtml(row.label)}</th>${latest.map((value) => `<td>${escapeHtml(formatFullAmount(value == null ? undefined : Number(value)))}</td>`).join('')}</tr>`
}

const staticStatementTableHtml = (
  title: string,
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
  rowLimit = 28,
): string => {
  const { periods } = statementPeriods(statement, periodCount)
  const rows = visiblePresentationStatementRows(statement, rowLimit, { collapseGroups: true })
  return `
    <section class="packet-section">
      <h2>${escapeHtml(title)}</h2>
      <table>
        <thead><tr><th></th>${periods.map((period) => `<th>${escapeHtml(formatPeriod(period))}</th>`).join('')}</tr></thead>
        <tbody>${rows.map((row) => statementRowHtml(row, periods.length)).join('')}</tbody>
      </table>
    </section>`
}

const staticRollupTableHtml = (
  title: string,
  subtitle: string,
  rollup: { periods: string[]; rows: FinancialRollupRow[] },
): string => `
  <section class="packet-section">
    <h2>${escapeHtml(title)}</h2>
    ${subtitle ? `<p class="section-note">${escapeHtml(subtitle)}</p>` : ''}
    <table>
      <thead><tr><th></th>${rollup.periods.map((period) => `<th>${escapeHtml(formatPeriod(period))}</th>`).join('')}</tr></thead>
      <tbody>${rollup.rows.map((row) => `
        <tr class="${row.emphasis === 'total' ? 'is-total' : row.emphasis === 'subtotal' ? 'is-subtotal' : row.emphasis === 'muted' ? 'is-muted' : ''}">
          <th>${escapeHtml(row.label)}</th>
          ${row.values.map((value) => `<td>${escapeHtml(formatRollupValue(value, row.kind))}</td>`).join('')}
        </tr>`).join('')}</tbody>
    </table>
  </section>`

const staticQuarterlyTrendHtml = (rollup: { periods: string[]; rows: FinancialRollupRow[] }): string => {
  const rowByKey = new Map(rollup.rows.map((row) => [row.key, row]))
  const quarters = new Map<string, { label: string; revenue: number; netIncome: number; ebitda: number; hasEbitda: boolean }>()
  rollup.periods.forEach((period, index) => {
    const date = new Date(`${period}T00:00:00`)
    if (Number.isNaN(date.getTime())) return
    const label = `Q${Math.floor(date.getMonth() / 3) + 1} ${String(date.getFullYear()).slice(-2)}`
    const current = quarters.get(label) || { label, revenue: 0, netIncome: 0, ebitda: 0, hasEbitda: false }
    const revenue = Number(rowByKey.get('revenue')?.values[index])
    const netIncome = Number(rowByKey.get('net-income')?.values[index])
    const ebitda = Number(rowByKey.get('ebitda')?.values[index])
    if (Number.isFinite(revenue)) current.revenue += revenue
    if (Number.isFinite(netIncome)) current.netIncome += netIncome
    if (Number.isFinite(ebitda)) {
      current.ebitda += ebitda
      current.hasEbitda = true
    }
    quarters.set(label, current)
  })
  const entries = Array.from(quarters.values()).filter((entry) => entry.revenue || entry.netIncome).slice(-QUARTERLY_TREND_MAX_QUARTERS)
  if (!entries.length) return ''
  const revenueMax = Math.max(1, ...entries.map((entry) => Math.max(0, entry.revenue)))
  const netIncomeValues = entries.map((entry) => entry.netIncome).filter((value) => Number.isFinite(value))
  const netIncomeMin = Math.min(0, ...netIncomeValues)
  const netIncomeMax = Math.max(0, ...netIncomeValues)
  const netIncomeSpan = Math.max(netIncomeMax - netIncomeMin, 1)
  const plotTop = 10
  const plotBottom = 88
  const zeroY = plotBottom - ((0 - netIncomeMin) / netIncomeSpan) * (plotBottom - plotTop)
  const netIncomePoints = entries.map((entry, index) => ({
    x: ((index + 0.5) / entries.length) * 100,
    y: plotBottom - ((entry.netIncome - netIncomeMin) / netIncomeSpan) * (plotBottom - plotTop),
    value: entry.netIncome,
    label: entry.label,
  }))
  return `
    <section class="packet-section quarterly-section">
      <h2>Quarterly Revenue & Net Income Trend</h2>
      <div class="quarterly-chart">
        <div class="quarterly-plot" style="grid-template-columns: repeat(${entries.length}, minmax(0, 1fr));">
          <svg class="quarterly-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <line class="quarterly-gridline" x1="0" x2="100" y1="${plotTop}" y2="${plotTop}" />
            <line class="quarterly-gridline" x1="0" x2="100" y1="${(plotTop + plotBottom) / 2}" y2="${(plotTop + plotBottom) / 2}" />
            <line class="quarterly-zero" x1="0" x2="100" y1="${zeroY}" y2="${zeroY}" />
            <polyline points="${escapeHtml(netIncomePoints.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' '))}" />
            ${netIncomePoints.map((point) => `<circle cx="${point.x.toFixed(2)}" cy="${point.y.toFixed(2)}" r="1.8"><title>${escapeHtml(`Net income ${formatAmount(point.value)} - ${point.label}`)}</title></circle>`).join('')}
          </svg>
          ${entries.map((entry) => `<div class="quarterly-bar-column">
            <div class="quarterly-revenue-value">${escapeHtml(formatAmount(entry.revenue))}</div>
            <div class="quarterly-revenue-bar" style="height: ${Math.max(5, Math.max(0, entry.revenue) / revenueMax * 100).toFixed(1)}%;"></div>
          </div>`).join('')}
        </div>
        <div class="quarterly-periods" style="grid-template-columns: repeat(${entries.length}, minmax(0, 1fr));">
          ${entries.map((entry) => `<div class="quarterly-period">
            <strong>${escapeHtml(entry.label)}</strong>
            <span class="${entry.netIncome < 0 ? 'negative' : ''}">NI ${escapeHtml(formatAmount(entry.netIncome))}</span>
            ${entry.hasEbitda ? `<span class="ebitda">EBITDA ${escapeHtml(formatAmount(entry.ebitda))}</span>` : ''}
          </div>`).join('')}
        </div>
        <div class="quarterly-legend"><span class="revenue">Revenue</span><span class="net-income">Net income</span></div>
      </div>
    </section>`
}

type WaterfallChartStep = {
  key: string
  label: string
  sublabel?: string
  value: number
  start: number
  end: number
  kind: 'start' | 'increase' | 'decrease' | 'end'
}

type WaterfallConfigEntry = {
  id: string
  title: string
  mode: 'mom' | 'yoy' | 'ytd' | string
  enabled?: boolean
}

const periodYear = (period: string): number | null => {
  const match = /^(\d{4})-\d{2}-\d{2}$/.exec(period)
  return match ? Number(match[1]) : null
}

const sumSeriesRange = (values: Array<number | null> | undefined, start: number, endInclusive: number): number | null => {
  if (!values || start < 0 || endInclusive < start) return null
  let total = 0
  let hasValue = false
  for (let index = start; index <= endInclusive; index += 1) {
    const value = Number(values[index])
    if (Number.isFinite(value)) {
      total += value
      hasValue = true
    }
  }
  return hasValue ? total : null
}

// Canonical net-income bridge numbers, sourced from the shared server-side
// metric + period comparison engine (server/src/integrations/quickbooks/fpa.rs
// `build_metric_comparisons`, exposed via
// ServerApiClient.getQuickBooksMetricComparisons). This is the single source of
// truth the CFO brief, ratio library, valuation, and workbook-wide agent all
// read, so the presentation waterfall must read the *same* deltas instead of
// recomputing them from the rendered statement series.
//
// Returns null when the engine has no usable bridge for this mode:
//  - `ytd` is a year-to-date composition view, not a period comparison, so the
//    engine intentionally does not model it (the caller renders the composition
//    from the statement series).
//  - `mom`/`yoy` return null when the comparison row is missing or its
//    current/prior net income is unobserved (e.g. insufficient history), in
//    which case the caller degrades to the statement-derived composition exactly
//    as before.
//
// Cost of sales prefers the canonical `cost_of_sales` comparison when present.
// Older cached responses may only include revenue + gross profit, so we keep the
// algebraic derivation as a fallback for those payloads.
export type WaterfallServerBridge = {
  priorNetIncome: number
  latestNetIncome: number
  netIncomeDelta: number
  revenueDelta: number
  costDelta: number
  expenseDelta: number
  priorPeriod: string
  latestPeriod: string
  priorPeriodStart: string
  priorPeriodEnd: string
  latestPeriodStart: string
  latestPeriodEnd: string
}

const waterfallComparisonKind = (mode: string): MetricComparisonKind | null => {
  if (mode === 'mom') return 'mom'
  if (mode === 'qoq') return 'qoq'
  if (mode === 'yoy') return 'yoy'
  if (mode === 'l3m_vs_prior3m') return 'l3m_vs_prior3m'
  if (mode === 'l12m_vs_prior12m') return 'l12m_vs_prior12m'
  if (mode === 'ytd') return null
  return 'mom'
}

const findMetricComparison = (
  comparisons: MetricComparisonResponse | null | undefined,
  metricId: string,
  kind: MetricComparisonKind,
): MetricComparison | null => {
  if (!comparisons || !Array.isArray(comparisons.metrics)) return null
  const row = comparisons.metrics.find((entry) => entry.id === metricId)
  if (!row || !Array.isArray(row.comparisons)) return null
  return row.comparisons.find((entry) => entry.kind === kind) || null
}

export const serverWaterfallBridge = (
  comparisons: MetricComparisonResponse | null | undefined,
  mode: string,
): WaterfallServerBridge | null => {
  const kind = waterfallComparisonKind(mode)
  if (!kind) return null
  const netIncome = findMetricComparison(comparisons, 'net_income', kind)
  if (!netIncome) return null
  const latestNetIncome = numberOrNull(netIncome.current_value)
  const priorNetIncome = numberOrNull(netIncome.prior_value)
  if (latestNetIncome == null || priorNetIncome == null) return null
  const revenue = findMetricComparison(comparisons, 'revenue', kind)
  const costOfSales = findMetricComparison(comparisons, 'cost_of_sales', kind)
  const grossProfit = findMetricComparison(comparisons, 'gross_profit', kind)
  const operatingExpenses = findMetricComparison(comparisons, 'operating_expenses', kind)
  const revenueDelta = numberOrNull(revenue?.delta_abs) ?? 0
  const directCostDelta = numberOrNull(costOfSales?.delta_abs)
  const grossProfitDelta = numberOrNull(grossProfit?.delta_abs) ?? 0
  const expenseDelta = numberOrNull(operatingExpenses?.delta_abs) ?? 0
  const priorPeriodStart = cleanText(netIncome.prior_period_start)
  const priorPeriodEnd = cleanText(netIncome.prior_period_end)
  const latestPeriodStart = cleanText(netIncome.current_period_start)
  const latestPeriodEnd = cleanText(netIncome.current_period_end)
  return {
    priorNetIncome,
    latestNetIncome,
    netIncomeDelta: numberOrNull(netIncome.delta_abs) ?? (latestNetIncome - priorNetIncome),
    revenueDelta,
    costDelta: directCostDelta ?? (revenueDelta - grossProfitDelta),
    expenseDelta,
    priorPeriod: priorPeriodStart || priorPeriodEnd,
    latestPeriod: latestPeriodStart || latestPeriodEnd,
    priorPeriodStart,
    priorPeriodEnd,
    latestPeriodStart,
    latestPeriodEnd,
  }
}

// Port of the live `Waterfall` component math (QuickBooksCfoPresentationTab.tsx)
// into a static SVG string so the exported HTML / slide deck render the same
// MoM / YoY / YTD net-income bridges that the interactive tab shows. Previously
// `staticReportBlockHtml` returned '' for `waterfall`, silently dropping these
// charts from every export — the explicit gap called out in the plan.
//
// MoM/YoY bridge deltas are sourced from the canonical server comparison engine
// (`serverWaterfallBridge`) when available so the export shows the exact same
// numbers as the brief/agent/tab. The statement-derived path remains for the
// YTD composition view and as graceful degradation when the comparison contract
// is unavailable (e.g. unit tests / pre-comparison-engine servers).
export type WaterfallBridgeComputation = {
  mode: 'mom' | 'yoy' | 'ytd'
  hasBridge: boolean
  steps: WaterfallChartStep[]
  priorNetIncome: number | null
  latestNetIncome: number
  netIncomeDelta: number
  priorPeriod: string
  latestPeriod: string
}

/**
 * Pure bridge computation shared by the HTML/SVG waterfall and the PPTX
 * export, so both always show the same columns/values: canonical server
 * comparisons when available, statement-derived deltas otherwise, and the
 * latest-period composition view for YTD/degraded windows.
 */
export const computeWaterfallBridge = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  metrics: any,
  periodCount: number,
  config: { mode?: string },
  comparisons?: MetricComparisonResponse | null,
): WaterfallBridgeComputation | null => {
  const mode = config.mode === 'yoy' || config.mode === 'ytd' ? config.mode : 'mom'
  const rollup = profitAndLossRollupRows(statement, periodCount)
  if (!rollup.periods.length) return null
  const byKey = new Map(rollup.rows.map((row) => [row.key, row]))
  const latestIndex = Math.max(0, rollup.periods.length - 1)
  const latestPeriodYear = periodYear(rollup.periods[latestIndex] || '')
  const ytdStartIndex = latestPeriodYear == null
    ? latestIndex
    : Math.max(0, rollup.periods.findIndex((period) => periodYear(period) === latestPeriodYear))
  const comparisonIndex = mode === 'yoy'
    ? latestIndex - 12
    : mode === 'ytd'
      ? ytdStartIndex
      : latestIndex - 1
  const valueAt = (key: string, index: number): number | null => {
    const value = Number(byKey.get(key)?.values[index])
    return Number.isFinite(value) ? value : null
  }
  const aggregateValue = (key: string, index: number): number | null => {
    if (mode !== 'ytd') return valueAt(key, index)
    return sumSeriesRange(byKey.get(key)?.values, ytdStartIndex, latestIndex)
  }
  const serverBridge = serverWaterfallBridge(comparisons, mode)
  const latestRevenue = aggregateValue('revenue', latestIndex) ?? metricAt(metrics, 'profitAndLoss.totalRevenue') ?? 0
  const latestCost = aggregateValue('cost-of-sales', latestIndex)
  const latestOperatingExpenses = aggregateValue('operating-expenses', latestIndex) ?? metricAt(metrics, 'profitAndLoss.operatingExpenses')
  const clientLatestNetIncome = aggregateValue('net-income', latestIndex) ?? metricAt(metrics, 'profitAndLoss.netIncome') ?? 0
  const clientPriorNetIncome = comparisonIndex >= 0 ? valueAt('net-income', comparisonIndex) : null
  const hasBridge = serverBridge != null || (mode !== 'ytd' && comparisonIndex >= 0 && clientPriorNetIncome != null)
  const latestNetIncome = serverBridge ? serverBridge.latestNetIncome : clientLatestNetIncome
  const priorNetIncome = serverBridge ? serverBridge.priorNetIncome : clientPriorNetIncome
  const latestPeriod = serverBridge ? serverBridge.latestPeriod : (rollup.periods[latestIndex] || '')
  const priorPeriod = serverBridge ? serverBridge.priorPeriod : (rollup.periods[comparisonIndex] || '')
  const delta = (key: string): number | null => {
    const latest = valueAt(key, latestIndex)
    const prior = comparisonIndex >= 0 ? valueAt(key, comparisonIndex) : null
    return latest != null && prior != null ? latest - prior : null
  }
  const revenueDelta = serverBridge ? serverBridge.revenueDelta : (delta('revenue') ?? 0)
  const costDelta = serverBridge ? serverBridge.costDelta : (delta('cost-of-sales') ?? 0)
  const expenseDelta = serverBridge ? serverBridge.expenseDelta : (delta('operating-expenses') ?? 0)
  const netIncomeDelta = serverBridge ? serverBridge.netIncomeDelta : (latestNetIncome - (priorNetIncome ?? latestNetIncome))
  const otherDelta = hasBridge ? netIncomeDelta - revenueDelta + costDelta + expenseDelta : 0
  const bridgeDrivers = hasBridge
    ? [
        { key: 'revenue', label: 'Revenue', value: revenueDelta },
        { key: 'cost-of-sales', label: 'Cost of Sales', value: -costDelta },
        { key: 'operating-expenses', label: 'OpEx', sublabel: 'Operating Expenses', value: -expenseDelta },
        ...(Math.abs(otherDelta) > 1 ? [{ key: 'other', label: 'Other', sublabel: 'Other P&L Movement', value: otherDelta }] : []),
      ]
    : [
        { key: 'cost-of-sales', label: 'Cost of Sales', value: -(latestCost || 0) },
        { key: 'operating-expenses', label: 'OpEx', sublabel: 'Operating Expenses', value: -(latestOperatingExpenses || 0) },
        { key: 'other', label: 'Other', sublabel: 'Other P&L Movement', value: latestNetIncome - latestRevenue + (latestCost || 0) + (latestOperatingExpenses || 0) },
      ].filter((entry) => Math.abs(entry.value) > 1)
  const startValue = hasBridge ? (priorNetIncome || 0) : latestRevenue
  let running = startValue
  const steps: WaterfallChartStep[] = [
    {
      key: 'start',
      label: hasBridge ? formatPeriod(priorPeriod) : (mode === 'ytd' ? 'YTD Revenue' : 'Revenue'),
      sublabel: hasBridge ? 'Net Income' : formatPeriod(latestPeriod),
      value: startValue,
      start: 0,
      end: startValue,
      kind: 'start',
    },
  ]
  for (const driver of bridgeDrivers) {
    const start = running
    const end = running + driver.value
    steps.push({
      key: driver.key,
      label: driver.label,
      sublabel: (driver as any).sublabel,
      value: driver.value,
      start,
      end,
      kind: driver.value >= 0 ? 'increase' : 'decrease',
    })
    running = end
  }
  steps.push({
    key: 'end',
    label: hasBridge ? formatPeriod(latestPeriod) : 'Net Income',
    sublabel: 'Net Income',
    value: latestNetIncome,
    start: 0,
    end: latestNetIncome,
    kind: 'end',
  })
  return {
    mode,
    hasBridge,
    steps,
    priorNetIncome: priorNetIncome ?? null,
    latestNetIncome,
    netIncomeDelta,
    priorPeriod,
    latestPeriod,
  }
}

const staticWaterfallHtml = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  metrics: any,
  periodCount: number,
  config: WaterfallConfigEntry,
  comparisons?: MetricComparisonResponse | null,
): string => {
  const computed = computeWaterfallBridge(statement, metrics, periodCount, config, comparisons)
  if (!computed) return ''
  const { mode, hasBridge, steps, latestNetIncome, netIncomeDelta, priorPeriod, latestPeriod } = computed
  const bridgeDrivers = steps.filter((step) => step.kind === 'increase' || step.kind === 'decrease')
  const startValue = steps[0]?.value ?? 0
  const priorNetIncome = computed.priorNetIncome
  const allValues = steps.flatMap((step) => [step.start, step.end])
  const totals = [startValue, latestNetIncome].filter((value) => Number.isFinite(value))
  const driverMagnitude = Math.max(...bridgeDrivers.map((driver) => Math.abs(driver.value)), 1)
  const minValue = Math.min(...allValues, 0)
  const maxValue = Math.max(...allValues, 0)
  const shouldTruncatePositiveAxis = minValue >= 0 && Math.max(...totals, 1) / driverMagnitude > 4
  const yFloor = shouldTruncatePositiveAxis
    ? Math.max(0, Math.min(...allValues) - driverMagnitude * 1.8)
    : Math.min(0, minValue)
  const yCeiling = maxValue + Math.max((maxValue - yFloor) * 0.12, driverMagnitude * 0.45, 1)
  const chart = { width: 1040, height: 380, left: 58, right: 26, top: 32, bottom: 76 }
  const plotWidth = chart.width - chart.left - chart.right
  const plotHeight = chart.height - chart.top - chart.bottom
  const span = Math.max(1, yCeiling - yFloor)
  const yFor = (value: number): number => chart.top + (yCeiling - value) / span * plotHeight
  const xStep = plotWidth / Math.max(1, steps.length)
  const barWidth = Math.min(76, Math.max(42, xStep * 0.52))
  const axisTicks = Array.from({ length: 5 }, (_, index) => yFloor + (span * index) / 4)
  const barGeometry = steps.map((step, index) => {
    const x = chart.left + xStep * index + (xStep - barWidth) / 2
    const base = step.kind === 'start' || step.kind === 'end'
      ? (shouldTruncatePositiveAxis ? yFloor : 0)
      : step.start
    const topValue = Math.max(base, step.end)
    const bottomValue = Math.min(base, step.end)
    const top = yFor(topValue)
    const bottom = yFor(bottomValue)
    return {
      step,
      x,
      y: top,
      width: barWidth,
      height: Math.max(3, bottom - top),
      connectorY: yFor(step.end),
      labelY: step.kind === 'start' || step.kind === 'end'
        ? top + Math.max(3, bottom - top) / 2 + 4
        : step.value >= 0
          ? Math.max(chart.top + 12, top - 10)
          : Math.min(chart.top + plotHeight - 8, bottom + 17),
    }
  })
  const insight = hasBridge
    ? `${formatPeriod(priorPeriod)} net income ${formatAmount(priorNetIncome)} moved ${formatSignedAmount(netIncomeDelta)} to ${formatAmount(latestNetIncome)} in ${formatPeriod(latestPeriod)}.`
    : `${mode === 'ytd' ? 'Year-to-date' : formatPeriod(latestPeriod)} revenue of ${formatAmount(startValue)} bridges to net income of ${formatAmount(latestNetIncome)}.`
  const axisBaselineY = yFor(shouldTruncatePositiveAxis ? yFloor : 0)
  const connectors = barGeometry.slice(0, -1).map((bar, index) => {
    const next = barGeometry[index + 1]
    return `<line class="wf-connector" x1="${(bar.x + bar.width).toFixed(2)}" x2="${next.x.toFixed(2)}" y1="${bar.connectorY.toFixed(2)}" y2="${bar.connectorY.toFixed(2)}" />`
  }).join('')
  const bars = barGeometry.map((bar) => `
    <g>
      <rect class="wf-column" data-kind="${bar.step.kind}" x="${bar.x.toFixed(2)}" y="${bar.y.toFixed(2)}" width="${bar.width.toFixed(2)}" height="${bar.height.toFixed(2)}" rx="3" />
      <text class="wf-value" data-kind="${bar.step.kind}" x="${(bar.x + bar.width / 2).toFixed(2)}" y="${bar.labelY.toFixed(2)}" text-anchor="middle">${escapeHtml(bar.step.kind === 'start' || bar.step.kind === 'end' ? formatAmount(bar.step.value) : formatSignedAmount(bar.step.value))}</text>
      <text class="wf-label" x="${(bar.x + bar.width / 2).toFixed(2)}" y="${(chart.height - 34).toFixed(2)}" text-anchor="middle">${escapeHtml(bar.step.label)}</text>
      ${bar.step.sublabel ? `<text class="wf-sublabel" x="${(bar.x + bar.width / 2).toFixed(2)}" y="${(chart.height - 18).toFixed(2)}" text-anchor="middle">${escapeHtml(bar.step.sublabel)}</text>` : ''}
    </g>`).join('')
  return `
    <section class="packet-section waterfall-section">
      <div class="waterfall-head"><h2>${escapeHtml(config.title || 'Net Income Bridge')}</h2><span>${escapeHtml(mode === 'yoy' ? 'Year-over-year movement' : mode === 'ytd' ? 'Year-to-date composition' : hasBridge ? 'Latest month movement' : 'Latest period composition')}</span></div>
      <div class="waterfall" role="img" aria-label="${escapeHtml(insight)}">
        <svg viewBox="0 0 ${chart.width} ${chart.height}" class="waterfall-svg">
          <line class="wf-axis" x1="${chart.left}" x2="${chart.width - chart.right}" y1="${axisBaselineY.toFixed(2)}" y2="${axisBaselineY.toFixed(2)}" />
          ${axisTicks.map((tick) => `<g><line class="wf-grid" x1="${chart.left}" x2="${chart.width - chart.right}" y1="${yFor(tick).toFixed(2)}" y2="${yFor(tick).toFixed(2)}" /><text class="wf-tick" x="${chart.left - 10}" y="${(yFor(tick) + 4).toFixed(2)}" text-anchor="end">${escapeHtml(formatAmount(tick))}</text></g>`).join('')}
          ${connectors}
          ${bars}
        </svg>
        <div class="waterfall-legend"><span><i data-kind="start"></i> Prior / starting total</span><span><i data-kind="increase"></i> Favorable</span><span><i data-kind="decrease"></i> Unfavorable</span><span><i data-kind="end"></i> Current total</span></div>
        <p class="waterfall-note">${escapeHtml(insight)}</p>
      </div>
    </section>`
}

const staticWaterfallsHtml = (
  response: QuickBooksCfoPresentationResponse,
  config: PresentationConfig,
  blockId: string,
  periodCount: number,
  comparisons?: MetricComparisonResponse | null,
  // Slide-deck pagination renders one waterfall per slide; when set, only the
  // matching configured waterfall is drawn (packet exports leave this unset
  // and keep rendering the full set).
  onlyWaterfallId?: string,
): string => {
  // The exported waterfalls must stay inside the same reporting window as the
  // rest of the packet (capped at MAX_EXPORTED_PRESENTATION_PERIOD_COUNT). Using
  // the body window keeps the static bridge consistent with the interactive tab
  // and prevents YoY from reaching back to a prior-year period that the exported
  // statements never display. When the window is too short for YoY/MoM, the
  // bridge math degrades gracefully to the latest-period composition view.
  const rawWaterfalls = Array.isArray((config as any).waterfalls) ? (config as any).waterfalls as WaterfallConfigEntry[] : []
  const waterfalls = (rawWaterfalls.length
    ? rawWaterfalls
    : [
        { id: 'mom-net-income', title: 'Net Income MoM Bridge', mode: 'mom', enabled: true },
        { id: 'yoy-net-income', title: 'Net Income YoY Bridge', mode: 'yoy', enabled: true },
        { id: 'ytd-net-income', title: 'Net Income YTD Bridge', mode: 'ytd', enabled: true },
      ] as WaterfallConfigEntry[]
  ).filter((entry) => entry && entry.enabled !== false)
    .filter((entry) => !onlyWaterfallId || entry.id === onlyWaterfallId)
  const html = waterfalls
    .map((entry) => staticWaterfallHtml(response.statements?.profit_and_loss, response.metrics || {}, periodCount, entry, comparisons))
    .filter(Boolean)
    .join('')
  if (!html) return ''
  return `<div data-report-block-id="${escapeHtml(blockId)}">${html}</div>`
}

// `part` supports the slide deck's two-page A/R split: 'overview' renders the
// commentary + KPI rows + DSO bars, 'detail' renders the collectability and
// customer tables. Packet exports pass 'all' (the default) and are unchanged.
const staticArTrendHtml = (
  response: QuickBooksCfoPresentationResponse,
  topCustomerLimit: number,
  commentary = '',
  part: 'all' | 'overview' | 'detail' = 'all',
): string => {
  const analysis = response.ar_analysis
  const periods = Array.isArray(analysis?.periods) ? analysis.periods : []
  const latest = periods.length ? periods[periods.length - 1] : null
  if (!latest) return ''
  const concerns = (Array.isArray(analysis?.topCustomerConcerns) ? analysis.topCustomerConcerns : [])
    .slice(0, Math.max(1, Math.min(20, topCustomerLimit)))
  const collectability = analysis?.collectability || null
  const thresholds = (Array.isArray(collectability?.thresholds) ? collectability.thresholds : [])
    .filter((threshold) => Number.isFinite(Number(threshold.thresholdDays)))
    .slice()
    .sort((a, b) => Number(a.thresholdDays) - Number(b.thresholdDays))
  const reserve = collectability?.badDebtReserve || null
  const reserveNumber = (value: number | null | undefined) => (
    response.public_projection && value == null ? null : numberOrNull(value)
  )
  const publicReserveValue = (value: number | null) => (
    response.public_projection ? value ?? undefined : value
  )
  const atRiskOpen = reserveNumber(reserve?.atRiskOpenAmount)
    ?? (response.public_projection ? null : (Array.isArray(collectability?.currentInvoices) ? collectability.currentInvoices : []).reduce((sum, invoice) => sum + Math.max(0, Number(invoice.openAmount || 0)), 0))
  const expectedCollectible = reserveNumber(reserve?.expectedCollectibleAmount)
  const expectedWriteOff = reserveNumber(reserve?.recommendedReserveAmount)
  const recoveryRate = response.public_projection && (expectedCollectible == null || atRiskOpen == null)
    ? null : safeClientRatio(expectedCollectible, atRiskOpen)
  const collectabilityByCustomer = new Map(
    (Array.isArray(collectability?.customers) ? collectability.customers : []).map((customer) => [
      cleanText(customer.publicCustomerKey) || cleanText(customer.customerId) || cleanText(customer.customer),
      customer,
    ]),
  )
  const maxDso = Math.max(1, ...periods.map((period) => Math.abs(Number(period.dso) || 0)))
  const showOverview = part !== 'detail'
  const showDetail = part !== 'overview' && analysis?.publicDetailAvailable !== false
  return `
    <section class="packet-section ar-section">
      <h2>${part === 'detail' ? 'A/R Detail' : 'A/R Trends'}</h2>
      ${showOverview && cleanText(commentary) ? `<div class="note ar-note">${inlineEmphasisHtml(commentary)}</div>` : ''}
      ${showOverview ? `<div class="kpis ar-kpis">
        <div class="kpi"><span>DSO</span><strong>${escapeHtml(formatDays(latest.dso))}</strong></div>
        <div class="kpi"><span>Open A/R</span><strong>${escapeHtml(formatAmount(latest.endingAr))}</strong></div>
        <div class="kpi"><span>Overdue A/R</span><strong>${escapeHtml(formatAmount(latest.overdueAr))}</strong></div>
        <div class="kpi"><span>Weighted Age</span><strong>${escapeHtml(formatDays(latest.weightedAverageAge))}</strong></div>
      </div>` : ''}
      ${showOverview && collectability ? `<div class="kpis ar-kpis">
        <div class="kpi"><span>At-risk A/R</span><strong>${escapeHtml(formatAmount(publicReserveValue(atRiskOpen)))}</strong></div>
        <div class="kpi"><span>Expected Collectible</span><strong>${escapeHtml(formatAmount(publicReserveValue(expectedCollectible)))}</strong></div>
        <div class="kpi"><span>Reserve Signal</span><strong>${escapeHtml(formatAmount(publicReserveValue(expectedWriteOff)))}</strong></div>
        <div class="kpi"><span>Recovery Rate</span><strong>${escapeHtml(formatPct(publicReserveValue(recoveryRate)))}</strong></div>
      </div>` : ''}
      ${showOverview ? `<div class="ar-bars">
        ${periods.map((period) => `
          <div class="ar-bar-col">
            <div class="ar-bar" style="height:${Math.max(10, Math.abs(Number(period.dso) || 0) / maxDso * 96)}px"></div>
            <strong>${escapeHtml(formatDays(period.dso))}</strong>
            <span>${escapeHtml(formatPeriod(period.periodStart))}</span>
          </div>`).join('')}
      </div>` : ''}
      ${showDetail && thresholds.length ? `<table>
        <thead><tr><th>Threshold</th><th>Open at Threshold</th><th>Recovery Rate</th><th>Full-Pay Rate</th><th>Confidence</th></tr></thead>
        <tbody>${thresholds.map((threshold) => `
          <tr>
            <th>${escapeHtml(`${Number(threshold.thresholdDays).toLocaleString()}+ days`)}</th>
            <td>${escapeHtml(formatFullAmount(threshold.openAtThresholdAmount))}</td>
            <td>${escapeHtml(formatPct(threshold.amountRecoveryRate))}</td>
            <td>${escapeHtml(formatPct(threshold.fullPaymentRate))}</td>
            <td>${escapeHtml(cleanText(threshold.confidence) || 'low')}</td>
          </tr>`).join('')}</tbody>
      </table>` : ''}
      ${showDetail ? `<table>
        <thead><tr><th>Customer</th><th>Open A/R</th><th>Overdue</th><th>90+ at Risk</th><th>Expected Write-Off</th><th>DSO</th><th>Weighted Age</th></tr></thead>
        <tbody>${concerns.length ? concerns.map((customer: NonNullable<QuickBooksArTrendAnalysis['topCustomerConcerns']>[number]) => {
          const collectabilityCustomer = collectabilityByCustomer.get(cleanText(customer.publicCustomerKey) || cleanText(customer.customerId) || cleanText(customer.customer))
          return `
          <tr>
            <th>${escapeHtml(customer.customer)}</th>
            <td>${escapeHtml(formatFullAmount(customer.endingAr))}</td>
            <td>${escapeHtml(formatFullAmount(customer.overdueAr))}</td>
            <td>${escapeHtml(formatFullAmount(collectabilityCustomer?.over90Amount))}</td>
            <td>${escapeHtml(formatFullAmount(collectabilityCustomer?.expectedWriteOffAmount))}</td>
            <td>${escapeHtml(formatDays(customer.dso))}</td>
            <td>${escapeHtml(formatDays(customer.weightedAverageAge))}</td>
          </tr>`
        }).join('') : '<tr><td colspan="7">No customer concerns identified.</td></tr>'}</tbody>
      </table>` : ''}
    </section>`
}

const statementForBlock = (response: QuickBooksCfoPresentationResponse, block: PresentationReportBlock): QuickBooksFpaStatementRender | null | undefined => {
  const statementType = cleanText(block.source?.statement_type || block.source?.statementType)
  if (statementType === 'balance_sheet') return response.statements?.balance_sheet
  if (statementType === 'cash_flow') return response.statements?.cash_flow
  return response.statements?.profit_and_loss
}

const rollupForBlock = (
  response: QuickBooksCfoPresentationResponse,
  block: PresentationReportBlock,
  periodCount: number,
  config: PresentationConfig,
): { periods: string[]; rows: FinancialRollupRow[] } => {
  const statementType = cleanText(block.source?.statement_type || block.source?.statementType)
  if (statementType === 'balance_sheet') return balanceSheetRollupRows(response.statements?.balance_sheet, periodCount)
  if (statementType === 'cash_flow') return cashFlowRollupRows(response.statements?.cash_flow, periodCount)
  return profitAndLossRollupRows(response.statements?.profit_and_loss, periodCount, config.derivedMetrics)
}

const staticKpiBlockHtml = (response: QuickBooksCfoPresentationResponse, block: PresentationReportBlock): string => {
  const metrics = response.metrics || {}
  if (block.section === 'balanceSheet') {
    return `<section class="kpis" data-report-block-id="${escapeHtml(block.id)}">
      <div class="kpi"><span>Cash</span><strong>${escapeHtml(formatAmount(metricAt(metrics, 'balanceSheet.cash')))}</strong></div>
      <div class="kpi"><span>Assets</span><strong>${escapeHtml(formatAmount(metricAt(metrics, 'balanceSheet.totalAssets')))}</strong></div>
      <div class="kpi"><span>Liabilities</span><strong>${escapeHtml(formatAmount(metricAt(metrics, 'balanceSheet.totalLiabilities')))}</strong></div>
      <div class="kpi"><span>Equity</span><strong>${escapeHtml(formatAmount(metricAt(metrics, 'balanceSheet.totalEquity')))}</strong></div>
    </section>`
  }
  if (block.section === 'cashFlow') {
    return `<section class="kpis" data-report-block-id="${escapeHtml(block.id)}">
      <div class="kpi"><span>Net cash change</span><strong>${escapeHtml(formatAmount(metricAt(metrics, 'cashFlow.netCashChange')))}</strong></div>
      <div class="kpi"><span>Ending cash</span><strong>${escapeHtml(formatAmount(metricAt(metrics, 'cashFlow.endingCash')))}</strong></div>
      <div class="kpi"><span>Revenue conversion</span><strong>${escapeHtml(formatPct(safeClientRatio(metricAt(metrics, 'cashFlow.netCashChange'), metricAt(metrics, 'profitAndLoss.totalRevenue'))))}</strong></div>
      <div class="kpi"><span>Runway signal</span><strong>${metricAt(metrics, 'cashFlow.endingCash') == null ? 'N/A' : ((metricAt(metrics, 'cashFlow.endingCash') || 0) >= 0 ? 'Positive' : 'Deficit')}</strong></div>
    </section>`
  }
  return `<section class="kpis" data-report-block-id="${escapeHtml(block.id)}">
    <div class="kpi"><span>Total revenue</span><strong>${escapeHtml(formatAmount(metricAt(metrics, 'profitAndLoss.totalRevenue')))}</strong></div>
    <div class="kpi"><span>Net income</span><strong>${escapeHtml(formatAmount(metricAt(metrics, 'profitAndLoss.netIncome')))}</strong></div>
    <div class="kpi"><span>EBITDA</span><strong>${escapeHtml(formatAmount(metricAt(metrics, 'profitAndLoss.ebitda')))}</strong></div>
    <div class="kpi"><span>Net margin</span><strong>${escapeHtml(formatPct(metricAt(metrics, 'profitAndLoss.netMarginPct')))}</strong></div>
  </section>`
}

const staticRatioCardHtml = (response: QuickBooksCfoPresentationResponse, block: PresentationReportBlock): string => {
  const model = buildRatioCardBlockModel((response as any).ratio_cards, block)
  if (!model) return ''
  const spark = buildRatioCardSparkline(model, 560, 170)
  const sparkHtml = spark ? `<div class="ratio-card-spark"><svg class="ratio-card-svg" viewBox="0 0 ${spark.width} ${spark.height}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="${escapeHtml(model.card.label)} 12-month trend">
      ${spark.benchmarkY != null ? `<line class="ratio-card-benchline" x1="0" y1="${spark.benchmarkY.toFixed(1)}" x2="${spark.width}" y2="${spark.benchmarkY.toFixed(1)}" />` : ''}
      <polyline class="ratio-card-line" points="${spark.linePoints}" />
      <circle class="ratio-card-dot" cx="${spark.points[0].x.toFixed(1)}" cy="${spark.points[0].y.toFixed(1)}" r="4" />
      <circle class="ratio-card-dot" cx="${spark.points[spark.points.length - 1].x.toFixed(1)}" cy="${spark.points[spark.points.length - 1].y.toFixed(1)}" r="4" />
      <text class="ratio-card-endlabel" x="${spark.firstLabel.x.toFixed(1)}" y="${spark.firstLabel.y.toFixed(1)}" text-anchor="start">${escapeHtml(spark.firstLabel.text)}</text>
      <text class="ratio-card-endlabel" x="${spark.lastLabel.x.toFixed(1)}" y="${spark.lastLabel.y.toFixed(1)}" text-anchor="end">${escapeHtml(spark.lastLabel.text)}</text>
    </svg>${model.benchmarkValue != null ? `<div class="ratio-card-benchcaption">${escapeHtml(model.benchmarkLabel)}: ${escapeHtml(formatRatioCardValue(model.benchmarkValue, model.unit))}</div>` : ''}</div>` : ''
  return `<section class="packet-section ratio-card" data-report-block-id="${escapeHtml(block.id)}" data-assessment="${escapeHtml(model.assessment)}">
    <h2>${escapeHtml(block.title || model.card.label)} <span class="ratio-card-pill" data-assessment="${escapeHtml(model.assessment)}">${escapeHtml(model.assessmentLabel)}</span>${model.directionLabel ? `<span class="ratio-card-direction">${escapeHtml(model.directionLabel)}</span>` : ''}</h2>
    ${model.explanation ? `<p class="ratio-card-reading">${escapeHtml(model.explanation)}</p>` : ''}
    ${model.formula ? `<div class="ratio-card-formula">${escapeHtml(model.formula)}</div>` : ''}
    <div class="kpis ratio-card-stats">
      ${model.stats.map((stat) => `<div class="kpi"><span>${escapeHtml(stat.label)}</span><strong>${escapeHtml(stat.value)}</strong></div>`).join('')}
    </div>
    <div class="ratio-card-body">
      ${sparkHtml}
      <table class="ratio-card-table">
        <thead><tr><th>Period</th><th>Current</th><th>Prior</th><th>Change</th></tr></thead>
        <tbody>${model.comparisonRows.map((row) => `<tr>${row.map((cell, idx) => `<${idx === 0 ? 'th' : 'td'}>${escapeHtml(cell)}</${idx === 0 ? 'th' : 'td'}>`).join('')}</tr>`).join('')}</tbody>
      </table>
    </div>
    ${model.note ? `<p class="ratio-card-note">${escapeHtml(model.note)}</p>` : ''}
  </section>`
}

const staticReportBlockHtml = (
  response: QuickBooksCfoPresentationResponse,
  block: PresentationReportBlock,
  config: PresentationConfig,
  periodCount: number,
  comparisons?: MetricComparisonResponse | null,
): string => {
  const commentary = config.commentary || {}
  if (block.kind === 'key_highlights') {
    const evidenceById = new Map((response.visual_evidence || []).map((item) => [item.id, item]))
    const requestedIndex = Number.isInteger(Number(block.source?.highlight_index)) ? Number(block.source?.highlight_index) : null
    const selectedHighlights = requestedIndex == null ? (response.key_highlights || []) : (response.key_highlights || []).slice(requestedIndex, requestedIndex + 1)
    const highlights = selectedHighlights.map((highlight, index) => {
      const visualEvidence = evidenceById.get(highlight.evidenceRef)
      if (!visualEvidence) return ''
      const state = highlight.changeState === 'new' ? 'New since review' : highlight.changeState.replace(/_/g, ' ')
      return `<article class="key-highlight${index === 0 && (response.key_highlights || []).length === 3 ? ' key-highlight-hero' : ''}">
        <div class="key-highlight-meta"><span>${escapeHtml(state)}</span><span>Through ${escapeHtml(highlight.dataThrough || 'latest data')}</span></div>
        <div class="key-highlight-value">${escapeHtml(highlight.valueDisplay)}</div>
        <h3>${escapeHtml(highlight.headline)}</h3>
        <div class="key-highlight-visual">${buildHighlightSvg(highlight.templateId, visualEvidence)}</div>
        <p>${escapeHtml(highlight.implication)}</p>
        ${highlight.principalFinding ? `<div class="key-highlight-action"><b>What moved</b>${escapeHtml(highlight.principalFinding)}</div>` : ''}
        <small>${escapeHtml(visualEvidence.provenanceState)} · ${escapeHtml(visualEvidence.sourceLineage.map((source) => source.replace(/^quickbooks_cfo_/, 'cfo_')).join(' · '))}</small>
      </article>`
    }).filter(Boolean)
    if (!highlights.length) return ''
    return `<section class="key-highlights" data-report-block-id="${escapeHtml(block.id)}"><header><div><span>Board briefing</span><h2>Key Highlights</h2></div><p>The changes and decisions that matter most right now.</p></header><div class="key-highlights-grid key-highlights-count-${highlights.length}">${highlights.join('')}</div></section>`
  }
  if (block.kind === 'kpi_grid') return staticKpiBlockHtml(response, block)
  if (block.kind === 'text') {
    const text = cleanText(block.source?.commentary) === 'executiveSummary'
      ? cleanText(commentary.executiveSummary)
      : cleanText(block.source?.commentary_section || block.source?.commentarySection)
        ? cleanText(commentary.sectionSummaries?.[cleanText(block.source?.commentary_section || block.source?.commentarySection)])
        : cleanText(block.content?.text)
    return text ? `<section class="note" data-report-block-id="${escapeHtml(block.id)}"><h2>${escapeHtml(block.title)}</h2><p>${inlineEmphasisHtml(text)}</p></section>` : ''
  }
  if (block.kind === 'statement_rollup') {
    return staticRollupTableHtml(block.title, '', rollupForBlock(response, block, periodCount, config)).replace('<section class="packet-section"', `<section class="packet-section" data-report-block-id="${escapeHtml(block.id)}"`)
  }
  if (block.kind === 'statement_detail') {
    return staticStatementTableHtml(
      block.title,
      statementForBlock(response, block),
      periodCount,
      Math.max(1, Math.min(80, Math.floor(Number(block.layout?.limit) || 32))),
    ).replace('<section class="packet-section"', `<section class="packet-section" data-report-block-id="${escapeHtml(block.id)}"`)
  }
  if (block.kind === 'waterfall') {
    return staticWaterfallsHtml(
      response,
      config,
      block.id,
      periodCount,
      comparisons,
      cleanText((block.source as any)?.waterfall_id) || undefined,
    )
  }
  if (block.kind === 'trend_chart' && cleanText(block.source?.metric) === 'quarterly_revenue_net_income') {
    return staticQuarterlyTrendHtml(profitAndLossRollupRows(response.statements?.profit_and_loss, QUARTERLY_TREND_MONTH_COUNT, config.derivedMetrics)).replace('<section class="packet-section"', `<section class="packet-section" data-report-block-id="${escapeHtml(block.id)}"`)
  }
  if (block.kind === 'ratio_card') return staticRatioCardHtml(response, block)
  if (block.kind === 'ar_summary') {
    const arPart = cleanText((block.source as any)?.ar_part)
    return staticArTrendHtml(
      response,
      config.receivables?.topCustomerLimit || 5,
      cleanText(commentary.sectionSummaries?.accountsReceivable),
      arPart === 'overview' || arPart === 'detail' ? arPart : 'all',
    ).replace('<section class="packet-section', `<section data-report-block-id="${escapeHtml(block.id)}" class="packet-section`)
  }
  if (block.kind === 'action_list') {
    const alerts = normalizeStringArray(commentary.alerts)
    const actions = normalizeStringArray(commentary.actions)
    const caveats = normalizeStringArray(commentary.dataCaveats)
    return [
      alerts.length ? `<section class="packet-section" data-report-block-id="${escapeHtml(block.id)}-alerts"><h2>Alerts</h2><ul>${alerts.map((item) => `<li>${inlineEmphasisHtml(item)}</li>`).join('')}</ul></section>` : '',
      actions.length ? `<section class="packet-section" data-report-block-id="${escapeHtml(block.id)}-actions"><h2>Priorities</h2><ul>${actions.map((item) => `<li>${inlineEmphasisHtml(item)}</li>`).join('')}</ul></section>` : '',
      caveats.length ? `<section class="packet-section" data-report-block-id="${escapeHtml(block.id)}-caveats"><h2>Data Caveats</h2><ul>${caveats.map((item) => `<li>${inlineEmphasisHtml(item)}</li>`).join('')}</ul></section>` : '',
    ].join('')
  }
  return ''
}

// Theme-aware stylesheet shared by the export packet and the slide deck. The
// theme tokens are emitted as CSS custom properties on `:root` (so the rest of
// the rules consume the exact same values the live preview uses) and the
// previously-hardcoded masthead / accent colors now reference those variables.
const presentationStyleBlock = (
  theme: ResolvedPresentationTheme,
  opts: { landscape: boolean; periodCount: number; pageSize: string; pageMargin: string; packetWidth: string; tableFontSize: string; tablePadding: string; firstColumnWidth: string },
): string => `
  ${VISUAL_FONT_FACE_CSS}
  ${visualRoleCssText()}
  @page { size: ${opts.pageSize}; margin: ${opts.pageMargin}; }
  :root { ${presentationThemeCssText(theme)} color: #1f2933; background: #fff; font-family: var(--fgcfo-heading-font); -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #fff; color: #1f2933; }
  .packet { max-width: ${opts.packetWidth}; margin: 0 auto; padding: 0; }
  .masthead { border-bottom: 4px solid var(--fgcfo-accent); background: var(--fgcfo-masthead-bg); color: var(--fgcfo-masthead-text); padding: 0.34in 0.38in; break-inside: avoid; page-break-inside: avoid; }
  .eyebrow { text-transform: uppercase; letter-spacing: .08em; color: var(--fgcfo-eyebrow); font: 700 10px Arial, sans-serif; }
  h1 { margin: 8px 0 6px; font-size: 30px; line-height: 1.05; font-family: var(--fgcfo-heading-font); }
  h2 { font-size: 18px; border-bottom: 1px solid #d8c8a8; padding-bottom: 7px; margin: 0 0 10px; break-after: avoid; page-break-after: avoid; font-family: var(--fgcfo-heading-font); }
  .meta { color: var(--fgcfo-masthead-meta); font: 12px Arial, sans-serif; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 18px 0; break-inside: avoid; page-break-inside: avoid; }
  .kpi { background: #fff; border: 1px solid #dccfb7; border-top: 3px solid var(--fgcfo-kpi-accent); padding: 12px; min-height: 86px; }
  .kpi span { display: block; color: #687386; font: 700 9px Arial, sans-serif; text-transform: uppercase; }
  .kpi strong { display: block; margin-top: 7px; font-size: 20px; line-height: 1; }
  .note { background: #fff; border: 1px solid #dccfb7; border-left: 4px solid var(--fgcfo-accent); padding: 13px 15px; line-height: 1.45; font-size: 12px; break-inside: avoid; page-break-inside: avoid; }
  .packet-section { margin-top: 18px; break-inside: avoid; page-break-inside: avoid; }
  .key-highlights { margin: 18px 0 24px; padding: 18px; border-radius: 14px; color: #f8fafc; background: #07111f; break-before: page; break-inside: avoid; page-break-inside: avoid; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .key-highlights > header { display: flex; align-items: end; justify-content: space-between; gap: 14px; margin-bottom: 12px; }
  .key-highlights > header span { color: #6ee7f2; font: 800 8px Arial,sans-serif; text-transform: uppercase; letter-spacing: .14em; }
  .key-highlights > header h2 { color: #fff; border: 0; margin: 2px 0 0; padding: 0; font-size: 22px; }
  .key-highlights > header p { color: #94a3b8; margin: 0; font-size: 9px; }
  .key-highlights-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 10px; }
  .key-highlights-count-1 { grid-template-columns: 1fr; }.key-highlight-hero { grid-row: span 2; }
  .key-highlight { padding: 12px; border: 1px solid #334155; border-radius: 10px; background: #111c2e; break-inside: avoid; }
  .key-highlight-meta { display: flex; justify-content: space-between; color: #94a3b8; font: 7px Arial,sans-serif; text-transform: capitalize; }
  .key-highlight-meta span:first-child { color: #07111f; background: #6ee7f2; border-radius: 99px; padding: 3px 6px; font-weight: 800; }
  .key-highlight-value { margin-top: 8px; color: #fff; font: 850 25px Arial,sans-serif; }.key-highlight h3 { margin: 1px 0 7px; color: #fff; font: 800 13px Arial,sans-serif; }
  .key-highlight-visual { padding: 3px; border-radius: 7px; background: #020617; }.key-highlight-visual svg { display: block; width: 100%; height: auto; max-height: 145px; }
  .key-highlight > p { color: #cbd5e1; font-size: 8px; line-height: 1.35; }.key-highlight-action { border-left: 2px solid #fbbf24; background: #252217; color: #e2e8f0; padding: 6px 8px; font-size: 8px; }.key-highlight-action b { display: block; color: #fbbf24; font-size: 6px; text-transform: uppercase; letter-spacing: .08em; }.key-highlight small { display: block; color: #64748b; margin-top: 7px; font-size: 6px; }
  .section-note { margin: -4px 0 8px; color: #667085; font: 10px Arial, sans-serif; }
  table { width: 100%; border-collapse: collapse; background: #fff; margin-top: 8px; table-layout: fixed; }
  thead { display: table-header-group; }
  tfoot { display: table-footer-group; }
  th, td { border-bottom: 1px solid #eadfcb; padding: ${opts.tablePadding}; text-align: right; font: ${opts.tableFontSize} Arial, sans-serif; overflow-wrap: anywhere; }
  th:first-child { text-align: left; width: ${opts.firstColumnWidth}; }
  td { white-space: nowrap; }
  tr.is-total th, tr.is-total td { font-weight: 800; border-top: 2px solid var(--fgcfo-accent); }
  tr.is-subtotal th, tr.is-subtotal td { font-weight: 800; border-top: 1px solid var(--fgcfo-accent); background: #fffaf0; }
  tr.is-muted th, tr.is-muted td { color: #667085; font-size: ${opts.landscape ? '7.5px' : '9px'}; }
  .quarterly-chart { position: relative; border: 1px solid #d9e2eb; border-radius: 8px; background: #f8fbfc; padding: 12px 12px 30px; break-inside: avoid; page-break-inside: avoid; }
  .quarterly-plot { position: relative; display: grid; gap: 7px; height: ${opts.landscape ? '170px' : '190px'}; align-items: end; padding-top: 14px; border-bottom: 1px solid #aab8c6; background: linear-gradient(to bottom, rgba(148, 163, 184, .18) 1px, transparent 1px) 0 14px / 100% 42px repeat-y; }
  .quarterly-lines { position: absolute; inset: 14px 0 0; width: 100%; height: calc(100% - 14px); overflow: visible; pointer-events: none; z-index: 2; }
  .quarterly-gridline { stroke: rgba(148, 163, 184, .32); stroke-width: .7; vector-effect: non-scaling-stroke; }
  .quarterly-zero { stroke: rgba(15, 23, 42, .34); stroke-width: 1; stroke-dasharray: 5 5; vector-effect: non-scaling-stroke; }
  .quarterly-lines polyline { fill: none; stroke: var(--fgcfo-masthead-bg); stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; vector-effect: non-scaling-stroke; }
  .quarterly-lines circle { fill: var(--fgcfo-masthead-bg); stroke: #fff; stroke-width: 1.2; vector-effect: non-scaling-stroke; }
  .quarterly-bar-column { position: relative; z-index: 1; height: 100%; display: flex; align-items: end; justify-content: center; min-width: 0; padding: 20px 5px 0; }
  .quarterly-revenue-value { position: absolute; top: 0; left: 1px; right: 1px; color: #415066; font: 800 7.5px Arial, sans-serif; text-align: center; white-space: nowrap; }
  .quarterly-revenue-bar { width: 100%; max-width: 34px; min-height: 10px; border-radius: 6px 6px 2px 2px; background: var(--fgcfo-accent); }
  .quarterly-periods { display: grid; gap: 7px; margin-top: 8px; }
  .quarterly-period { display: grid; gap: 2px; min-width: 0; color: #344054; font: 700 7.5px Arial, sans-serif; line-height: 1.15; overflow-wrap: anywhere; }
  .quarterly-period strong { color: #111827; font-size: 9px; }
  .quarterly-period span { color: var(--fgcfo-masthead-bg); }
  .quarterly-period span.negative { color: #b42318; }
  .quarterly-period span.ebitda { color: #667085; font-size: 7.5px; }
  .quarterly-legend { position: absolute; left: 12px; right: 12px; bottom: 9px; display: flex; gap: 12px; color: #344054; font: 800 8px Arial, sans-serif; }
  .quarterly-legend span { display: inline-flex; align-items: center; gap: 4px; }
  .quarterly-legend span::before { content: ""; width: 14px; height: 3px; border-radius: 999px; background: var(--fgcfo-masthead-bg); }
  .quarterly-legend .revenue::before { height: 8px; border-radius: 2px; background: var(--fgcfo-accent); }
  .waterfall-section { margin-top: 18px; }
  .waterfall-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; border-bottom: 1px solid #d8c8a8; padding-bottom: 7px; margin-bottom: 10px; }
  .waterfall-head h2 { border: 0; padding: 0; margin: 0; }
  .waterfall-head span { color: #687386; font: 700 9px Arial, sans-serif; text-transform: uppercase; letter-spacing: .06em; }
  .waterfall { border: 1px solid #d9e2eb; border-radius: 8px; background: #f8fbfc; padding: 12px; break-inside: avoid; page-break-inside: avoid; }
  .waterfall-svg { width: 100%; height: auto; }
  .wf-axis { stroke: rgba(15,23,42,.45); stroke-width: 1; }
  .wf-grid { stroke: rgba(148,163,184,.32); stroke-width: .7; }
  .wf-tick { fill: #687386; font: 10px Arial, sans-serif; }
  .wf-connector { stroke: rgba(15,23,42,.32); stroke-width: 1.2; stroke-dasharray: 4 4; }
  .wf-column[data-kind="start"], .wf-column[data-kind="end"] { fill: var(--fgcfo-masthead-bg); }
  .wf-column[data-kind="increase"] { fill: #2f855a; }
  .wf-column[data-kind="decrease"] { fill: #c0392b; }
  .wf-value { fill: #1f2933; font: 700 13px Arial, sans-serif; }
  .wf-value[data-kind="start"], .wf-value[data-kind="end"] { fill: #fff; }
  .wf-label { fill: #344054; font: 700 12px Arial, sans-serif; }
  .wf-sublabel { fill: #687386; font: 10px Arial, sans-serif; }
  .waterfall-legend { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 8px; color: #344054; font: 700 9px Arial, sans-serif; }
  .waterfall-legend span { display: inline-flex; align-items: center; gap: 5px; }
  .waterfall-legend i { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
  .waterfall-legend i[data-kind="start"], .waterfall-legend i[data-kind="end"] { background: var(--fgcfo-masthead-bg); }
  .waterfall-legend i[data-kind="increase"] { background: #2f855a; }
  .waterfall-legend i[data-kind="decrease"] { background: #c0392b; }
  .waterfall-note { margin: 8px 0 0; color: #475467; font: 11px Arial, sans-serif; }
  .ar-kpis { margin-top: 8px; }
  .ar-note { margin-bottom: 10px; }
  .ar-bars { display: grid; grid-template-columns: repeat(${Math.max(1, Math.min(opts.periodCount, 24))}, minmax(34px, 1fr)); gap: 7px; align-items: stretch; min-height: 154px; margin: 10px 0 12px; }
  .ar-bar-col { display: grid; grid-template-rows: 100px 22px 24px; gap: 4px; align-items: end; justify-items: center; font: 8px Arial, sans-serif; color: #687386; }
  .ar-bar { width: 100%; max-width: 22px; min-height: 10px; border-radius: 4px 4px 0 0; background: var(--fgcfo-masthead-bg); }
  .ar-bar-col strong { color: #1f2933; font-size: 8px; line-height: 1.15; min-height: 20px; text-align: center; align-self: start; }
  .ar-bar-col span { line-height: 1.15; min-height: 22px; text-align: center; align-self: start; }
  .ratio-card { border: 1px solid #dccfb7; border-left: 4px solid #94a3b8; border-radius: 6px; background: #fff; padding: 14px 16px; break-inside: avoid; page-break-inside: avoid; }
  .ratio-card[data-assessment="healthy"] { border-left-color: #2f855a; }
  .ratio-card[data-assessment="watch"] { border-left-color: #b45309; }
  .ratio-card[data-assessment="concern"] { border-left-color: #b42318; }
  .ratio-card h2 { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
  .ratio-card-pill { border-radius: 999px; padding: 2px 9px; font: 800 9px Arial, sans-serif; text-transform: uppercase; letter-spacing: .04em; color: #475467; background: #eef2f6; }
  .ratio-card-pill[data-assessment="healthy"] { color: #14532d; background: #e5f4ea; }
  .ratio-card-pill[data-assessment="watch"] { color: #92400e; background: #fdf1e0; }
  .ratio-card-pill[data-assessment="concern"] { color: #b42318; background: #fdebe9; }
  .ratio-card-direction { color: #687386; font: 700 9px Arial, sans-serif; text-transform: uppercase; letter-spacing: .04em; }
  .ratio-card-reading { margin: 8px 0 0; color: #344054; font: 12px/1.45 Arial, sans-serif; }
  .ratio-card-formula { margin-top: 6px; color: #687386; font: 700 10px Arial, sans-serif; }
  .ratio-card-stats { grid-template-columns: repeat(3, 1fr); margin: 12px 0; }
  .ratio-card-body { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr); gap: 14px; align-items: start; }
  .ratio-card-spark { border: 1px solid #d9e2eb; border-radius: 8px; background: #f8fbfc; padding: 8px; }
  .ratio-card-svg { width: 100%; height: auto; display: block; }
  .ratio-card-line { fill: none; stroke: var(--fgcfo-masthead-bg); stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
  .ratio-card-dot { fill: var(--fgcfo-masthead-bg); stroke: #fff; stroke-width: 1.4; }
  .ratio-card-benchline { stroke: #94a3b8; stroke-width: 1.4; stroke-dasharray: 5 5; }
  .ratio-card-endlabel { fill: #344054; font: 700 11.5px Arial, sans-serif; }
  .ratio-card-benchcaption { margin-top: 4px; color: #687386; font: 650 10px Arial, sans-serif; }
  .ratio-card-table { table-layout: auto; margin-top: 0; }
  .ratio-card-table th:first-child { width: auto; }
  .ratio-card-table tbody th { text-align: left; font-weight: 650; }
  .ratio-card-note { margin: 10px 0 0; color: #7c8798; font: 10px/1.4 Arial, sans-serif; }
  ul { background: #fff; border: 1px solid #dccfb7; padding: 13px 20px; line-height: 1.4; font-size: 12px; break-inside: avoid; page-break-inside: avoid; }
  .pdf-footer { margin-top: 22px; padding-top: 9px; border-top: 1px solid #d8c8a8; color: #687386; font: 10px Arial, sans-serif; }`

export const buildQuickBooksCfoPresentationPdfHtml = (
  response: QuickBooksCfoPresentationResponse,
  comparisons?: MetricComparisonResponse | null,
): string => {
  const config = normalizeConfig(response.config)
  const theme = resolvePresentationTheme(config.style)
  const title = displayReportTitle(config.branding?.reportTitle || config.reportTitle)
  const companyName = cleanText(config.branding?.companyDisplayName) || response.company_name || 'Client'
  const subtitle = cleanText(config.branding?.subtitle)
  const preparedTitle = cleanText(config.branding?.preparedReportTitle) || 'Prepared Report'
  const period = statementLatestPeriod(response.statements?.profit_and_loss)
  const periodCount = Math.max(1, Math.min(MAX_EXPORTED_PRESENTATION_PERIOD_COUNT, Math.floor(Number(config.periodCount) || DEFAULT_PRESENTATION_PERIOD_COUNT)))
  const blockHtml = normalizeReportBlocks(config.reportBlocks)
    .filter((block) => block.enabled !== false)
    .map((block) => staticReportBlockHtml(response, block, config, periodCount, comparisons))
    .filter(Boolean)
    .join('')
  const landscape = periodCount >= 9
  const styleBlock = presentationStyleBlock(theme, {
    landscape,
    periodCount,
    pageSize: landscape ? 'Letter landscape' : 'Letter',
    pageMargin: landscape ? '0.34in' : '0.55in',
    packetWidth: landscape ? '10.2in' : '7.4in',
    tableFontSize: landscape ? '8px' : '10px',
    tablePadding: landscape ? '4px 4px' : '6px 7px',
    firstColumnWidth: landscape ? '1.72in' : '2.15in',
  })
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(title)}</title>
<style>
${styleBlock}
  @media screen {
    body { background: var(--fgcfo-page-bg); padding: 24px; }
    .packet { background: #fff; box-shadow: 0 16px 40px rgba(15, 23, 42, .16); padding: .55in; }
  }
  @media print {
    body { background: #fff; }
    .packet { width: 100%; max-width: none; }
  }
</style>
</head>
<body>
  <main class="packet">
    <header class="masthead">
      <div class="eyebrow">Performance review</div>
      <h1>${escapeHtml(title)}</h1>
      <div class="meta">${escapeHtml(companyName)} ${period ? `| ${escapeHtml(period)}` : ''}${subtitle ? ` | ${escapeHtml(subtitle)}` : ''}</div>
    </header>
    ${blockHtml}
    <footer class="pdf-footer">${escapeHtml(preparedTitle)} from FuseGrid Financial Review.</footer>
  </main>
</body>
</html>`
}

export const buildQuickBooksCfoPresentationHtml = buildQuickBooksCfoPresentationPdfHtml

// Curated slide-deck rendering, composed from the same themed block HTML as the
// export packet. Slide pagination comes from the shared deck model
// (cfo/presentation/deckModel.ts): each enabled section gets as many slides as
// its blocks need, so nothing clips off the bottom of a fixed 16:9 page. Slides
// print one Letter-landscape page each and are navigable on screen via
// prev/next + arrow keys. The PPTX export consumes the SAME model, so both
// decks paginate identically.
export const buildQuickBooksCfoPresentationDeckHtml = (
  response: QuickBooksCfoPresentationResponse,
  comparisons?: MetricComparisonResponse | null,
): string => {
  const config = normalizeConfig(response.config)
  const theme = resolvePresentationTheme(config.style)
  const title = displayReportTitle(config.branding?.reportTitle || config.reportTitle)
  const companyName = cleanText(config.branding?.companyDisplayName) || response.company_name || 'Client'
  const subtitle = cleanText(config.branding?.subtitle)
  const period = statementLatestPeriod(response.statements?.profit_and_loss)
  const periodCount = Math.max(1, Math.min(MAX_EXPORTED_PRESENTATION_PERIOD_COUNT, Math.floor(Number(config.periodCount) || DEFAULT_PRESENTATION_PERIOD_COUNT)))
  const sectionEnabled = (config as any).sections && typeof (config as any).sections === 'object' ? (config as any).sections : {}
  const sectionConfig = config.sectionConfig || {}
  const blocks = normalizeReportBlocks(config.reportBlocks).filter((block) => block.enabled !== false)
  const styleBlock = presentationStyleBlock(theme, {
    landscape: true,
    periodCount,
    pageSize: 'Letter landscape',
    pageMargin: '0',
    packetWidth: '100%',
    tableFontSize: '11px',
    tablePadding: '6px 8px',
    firstColumnWidth: '2.4in',
  })
  const titleSlide = `
    <section class="slide slide-title" data-slide="0">
      <div class="slide-title-inner">
        <div class="eyebrow">Performance review</div>
        <h1>${escapeHtml(title)}</h1>
        <p class="slide-title-meta">${escapeHtml(companyName)}${period ? ` &middot; ${escapeHtml(period)}` : ''}${subtitle ? ` &middot; ${escapeHtml(subtitle)}` : ''}</p>
      </div>
    </section>`
  // Paginate blocks across slides via the shared deck model: a section gets as
  // many slides as its blocks need (fixing the old one-slide-per-section layout
  // that clipped anything taller than one 16:9 page), multi-waterfall blocks
  // split one waterfall per slide, and detail tables are clamped to slide rows.
  const sectionSlides = buildPresentationDeckSlides({
    ...(config as any),
    sections: sectionEnabled,
    sectionConfig,
    reportBlocks: blocks,
    keyHighlightCount: response.key_highlights?.length || 0,
  })
    .filter((slide): slide is DeckContentSlide => slide.kind === 'content')
    .map((slide) => {
      const body = slide.blocks
        .map((block) => staticReportBlockHtml(response, block as PresentationReportBlock, config, periodCount, comparisons))
        .filter(Boolean)
        .join('')
      if (!body) return ''
      return `
        <section class="slide" data-section="${escapeHtml(slide.sectionKey)}">
          <div class="slide-head"><div class="eyebrow">${escapeHtml(title)}</div><h2 class="slide-h2">${escapeHtml(slide.heading)}${slide.continued ? '<span class="slide-continued"> · continued</span>' : ''}</h2></div>
          <div class="slide-body">${body}</div>
        </section>`
    })
    .filter(Boolean)
  const slides = [titleSlide, ...sectionSlides]
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(title)} — Slides</title>
<style>
${styleBlock}
  body { background: #1b1b1f; }
  .deck { max-width: 1180px; margin: 0 auto; padding: 18px 18px 70px; }
  .slide { position: relative; background: #fff; color: #1f2933; border-radius: 10px; box-shadow: 0 20px 50px rgba(0,0,0,.35); margin: 0 auto 22px; padding: 30px 36px; width: 100%; aspect-ratio: 16 / 9; overflow: hidden; display: flex; flex-direction: column; }
  .slide-head { border-bottom: 3px solid var(--fgcfo-accent); padding-bottom: 8px; margin-bottom: 14px; }
  .slide-head .eyebrow { color: var(--fgcfo-accent); }
  .slide-h2 { border: 0; padding: 0; margin: 4px 0 0; font-size: 26px; }
  .slide-continued { color: #98a2b3; font-size: 15px; font-weight: 600; }
  .slide-body { flex: 1; min-height: 0; overflow: hidden; }
  .slide-body .packet-section, .slide-body .kpis, .slide-body .note, .slide-body .waterfall-section { margin-top: 12px; }
  .slide-body .kpis { margin: 0 0 12px; }
  .slide-title { background: var(--fgcfo-masthead-bg); color: var(--fgcfo-masthead-text); align-items: flex-start; justify-content: center; }
  .slide-title .eyebrow { color: var(--fgcfo-eyebrow); }
  .slide-title h1 { color: var(--fgcfo-masthead-text); font-size: 46px; margin: 10px 0; }
  .slide-title-meta { color: var(--fgcfo-masthead-meta); font: 15px Arial, sans-serif; }
  .deck-nav { position: fixed; left: 0; right: 0; bottom: 0; display: flex; align-items: center; justify-content: center; gap: 14px; padding: 10px; background: rgba(17,17,20,.92); color: #fff; font: 700 13px Arial, sans-serif; z-index: 10; }
  .deck-nav button { height: 30px; padding: 0 14px; border-radius: 6px; border: 1px solid #4b5563; background: #2b2b31; color: #fff; cursor: pointer; font-weight: 700; }
  .deck-nav button:disabled { opacity: .45; cursor: not-allowed; }
  .deck-count { min-width: 70px; text-align: center; }
  @media print {
    @page { size: Letter landscape; margin: 0; }
    body { background: #fff; }
    .deck { max-width: none; padding: 0; }
    .deck-nav { display: none; }
    .slide { box-shadow: none; border-radius: 0; margin: 0; aspect-ratio: auto; width: 100%; height: 100vh; page-break-after: always; break-after: page; }
  }
</style>
</head>
<body>
  <div class="deck" data-testid="cfo-presentation-deck">
    ${slides.join('')}
  </div>
  <div class="deck-nav">
    <button type="button" id="deck-prev">Prev</button>
    <span class="deck-count" id="deck-count"></span>
    <button type="button" id="deck-next">Next</button>
    <button type="button" id="deck-print">Print / PDF</button>
  </div>
  <script>
    (function () {
      var slides = Array.prototype.slice.call(document.querySelectorAll('.slide'));
      var index = 0;
      var countEl = document.getElementById('deck-count');
      var prevEl = document.getElementById('deck-prev');
      var nextEl = document.getElementById('deck-next');
      function show(i) {
        index = Math.max(0, Math.min(slides.length - 1, i));
        slides[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
        countEl.textContent = (index + 1) + ' / ' + slides.length;
        prevEl.disabled = index <= 0;
        nextEl.disabled = index >= slides.length - 1;
      }
      prevEl.addEventListener('click', function () { show(index - 1); });
      nextEl.addEventListener('click', function () { show(index + 1); });
      document.getElementById('deck-print').addEventListener('click', function () { window.print(); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'PageDown') { show(index + 1); }
        else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { show(index - 1); }
      });
      show(0);
    })();
  </script>
</body>
</html>`
}
